<script setup lang="ts">

import PayoutDateInput from "@/components/forms/payout/forms/PayoutDateInput.vue";
import HorizontalFormGroup from "@/components/forms/horizontal/HorizontalFormGroup.vue";
import MultipleDocumentFilesTableUpload
  from "@/components/forms/files/MultipleDocumentFilesTableUpload.vue";
import PayoutInputHintBlock from "@/components/forms/payout/PayoutInputHintBlock.vue";
import DropdownKeyvalue from "@/components/forms/dropdown/DropdownKeyValue.vue";
import {computed, getCurrentInstance, ref, watch} from "vue";
import VueI18n from "vue-i18n";
import moment from "moment-timezone";
import ErrorMessageBlock from "@/components/forms/payout/ErrorMessageBlock.vue";

const props = defineProps({
  maxFilesPerDocument: {
    type: Number,
    default: 3
  },
  value: {
    type: Array,
    default: () => []
  },
  disabled: {
    type: Boolean,
    default: false
  },
  model: {
    type: Object,
    default: null
  },
  modelAttributesPrefix: {
    type: String,
    default: 'addressDocument'
  }
})

const addressDocumentTypeOptions = computed(() => {
  const i18n = getCurrentInstance()?.proxy.$i18n as VueI18n;

  const values = {
    ['DOMESTIC_UTILITY_BILL']: 'Коммунальная квитанция',
    ['INTERNATIONAL_UTILITY_BILL']: 'Заграничная коммунальная квитанция',
    ['TELEPHONE_BILL']: 'Телефонный счет',
    ['BANK_ACCOUNT_STATEMENT']: 'Выписка из банковского счета',
  }

  const result = {};

  Object.keys(values).forEach((item) => {
    result[item] = i18n ? i18n.t(values[item]) : values[item];
  });

  return result;
})

const addressDocumentDatePickerOptions = computed(() => {
  const i18n = getCurrentInstance()?.proxy.$i18n as VueI18n;

  return {
    range: false,
    dateFormat: 'dd.MM.yyyy',
    minDate: moment().subtract(5, 'months').toDate(),
    maxDate: moment().toDate(),
    view: 'days',
    //minView: 'months',
    autoClose: true,
    language: i18n ? i18n.locale : 'ru'
    //language: //document.getElementsByTagName("html")[0].getAttribute("lang")
  }
})

const addressDocumentType = ref('');
const addressDocumentValidFrom = ref('');
const addressDocumentFiles = ref([]);

const documentInfo = computed(() => {
  return addressDocumentFiles.value.map((file) => {
    return {
      id: file.id,
      file: file,
      type: addressDocumentType.value,
      validFrom: addressDocumentValidFrom.value
    }
  })
})

const resetDocument = () => {
  addressDocumentType.value = '';
  addressDocumentValidFrom.value = '';
  addressDocumentFiles.value = [];
}

const emits = defineEmits(['input']);

watch(() => [addressDocumentType.value, addressDocumentValidFrom.value,
  addressDocumentFiles.value.join(',')].join(':'), (value) => {
  emits('input', documentInfo.value);
})

const isUploading = () => {
  return addressDocumentFiles.value.some((file) => file.uploading);
}

const documentSelector = ref(null)

const busy = computed(() => {
 return documentSelector.value.busy || isUploading();
})

defineExpose({documentInfo, resetDocument, busy})
</script>

<template>
  <div v-if="props.model">
    <HorizontalFormGroup :label="$t('Тип документа')"
                         :has-errors="props.model.errors.has(props.modelAttributesPrefix + 'type')"
                         :required="true">
      <DropdownKeyvalue :options="addressDocumentTypeOptions"
                        v-model="addressDocumentType"
                        :disabled="disabled"
                        :placeholder="$t('Выберите тип документа')"/>
      <error-message-block v-slot:errors
                           :attribute="props.modelAttributesPrefix + 'type'"
                           :errors="props.model.errors"></error-message-block>
    </HorizontalFormGroup>

    <HorizontalFormGroup :label="$t('Дата выдачи')"
                         :required="true">
      <payout-date-input v-model="addressDocumentValidFrom"
                         ref="expirationDate"
                         :disabled="disabled"
                         :picker-options="addressDocumentDatePickerOptions"
                         :init-value="addressDocumentValidFrom"
                         class="form-control"/>
      <payout-input-hint-block v-slot:hints
                               :hint="$t('Дата выдачи не должна быть старше 2 месяцев. Дата в формате ДД.ММ.ГГГГ.')"></payout-input-hint-block>
      <error-message-block v-slot:errors
                           :attribute="props.modelAttributesPrefix + 'validFrom'"
                           :errors="props.model.errors"></error-message-block>
    </HorizontalFormGroup>

    <HorizontalFormGroup :label="$t('Копии документов')" :required="true"
                         :has-errors="props.model.errors.has(props.modelAttributesPrefix + 'id')"
      >
      <MultipleDocumentFilesTableUpload
        accept="application/pdf,image/jpeg,image/png"
        :maxFileSize="10485760"
        file-upload-url="/webapi/profile/update?call=uploadFile"
        v-model="addressDocumentFiles"
        ref="documentSelector"
        :disabled="disabled"></MultipleDocumentFilesTableUpload>
      <error-message-block v-slot:errors
                           :attribute="props.modelAttributesPrefix + 'id'"
                           :errors="props.model.errors"></error-message-block>
    </HorizontalFormGroup>

  </div>
</template>

<style scoped lang="less">

</style>

<i18n>
{
  "en": {
    "Коммунальная квитанция": "Utility bill",
    "Заграничная коммунальная квитанция": "International utility bill",
    "Телефонный счет": "Telephone bill",
    "Выписка из банковского счета": "Bank account statement",
    "Тип документа": "Document type",
    "Выберите тип документа": "Select document type",
    "Дата выдачи": "Date of issue",
    "Дата выдачи не должна быть старше 2 месяцев. Дата в формате ДД.ММ.ГГГГ.": "The date of issue must not be more than 2 months old. Date in format DD.MM.YYYY.",
    "Копии документов": "Copies of documents"
  }
}
</i18n>
